import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import ChevronRightIcon from '../../assets/chevron_right.svg'
import styles from './Carousel.module.css'

interface CarouselProps<T> {
  items: T[]
  renderItem: (item: T, index: number) => ReactNode
  keyExtractor: (item: T, index: number) => string
  cardsPerSlide?: number
  cardWidth?: number
  cardGap?: number
  autoHideWhenFits?: boolean
  onCardsPerSlideChange?: (cardsPerSlide: number) => void
  className?: string
}

export function Carousel<T>({
  items,
  renderItem,
  keyExtractor,
  cardsPerSlide: fixedCardsPerSlide,
  cardWidth = 164,
  cardGap = 10,
  autoHideWhenFits = false,
  onCardsPerSlideChange,
  className = '',
}: CarouselProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [needsCarousel, setNeedsCarousel] = useState(!autoHideWhenFits)
  const [calculatedCardsPerSlide, setCalculatedCardsPerSlide] = useState(items.length)
  const [currentSlide, setCurrentSlide] = useState(0)

  const cardsPerSlide = fixedCardsPerSlide ?? calculatedCardsPerSlide

  const calculateLayout = useCallback(() => {
    if (!containerRef.current || fixedCardsPerSlide) return

    const containerWidth = containerRef.current.offsetWidth
    const totalCardsWidth = items.length * cardWidth + (items.length - 1) * cardGap

    if (autoHideWhenFits && totalCardsWidth <= containerWidth) {
      setNeedsCarousel(false)
      setCalculatedCardsPerSlide(items.length)
      onCardsPerSlideChange?.(items.length)
    } else {
      setNeedsCarousel(true)
      const navButtonsWidth = 140
      const availableWidth = containerWidth - navButtonsWidth
      const fittingCards = Math.max(1, Math.floor((availableWidth + cardGap) / (cardWidth + cardGap)))
      setCalculatedCardsPerSlide(fittingCards)
      onCardsPerSlideChange?.(fittingCards)
    }
    setCurrentSlide(0)
  }, [items.length, cardWidth, cardGap, autoHideWhenFits, fixedCardsPerSlide, onCardsPerSlideChange])

  useEffect(() => {
    if (autoHideWhenFits && !fixedCardsPerSlide) {
      calculateLayout()

      const resizeObserver = new ResizeObserver(() => {
        calculateLayout()
      })

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [calculateLayout, autoHideWhenFits, fixedCardsPerSlide])

  useEffect(() => {
    if (fixedCardsPerSlide) {
      setCurrentSlide(0)
    }
  }, [fixedCardsPerSlide])

  const totalSlides = Math.ceil(items.length / cardsPerSlide)

  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1))
  }

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0))
  }

  const goToSlide = (slideIndex: number) => {
    setCurrentSlide(slideIndex)
  }

  const getCurrentSlideItems = () => {
    const startIndex = currentSlide * cardsPerSlide
    return items.slice(startIndex, startIndex + cardsPerSlide)
  }

  if (autoHideWhenFits && !needsCarousel) {
    return (
      <div ref={containerRef} className={`${styles.container} ${className}`}>
        <div className={styles.cardsRow}>
          {items.map((item, index) => (
            <div key={keyExtractor(item, index)}>{renderItem(item, index)}</div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={`${styles.container} ${className}`}>
      <div className={styles.carouselWrapper}>
        <button
          className={styles.navButton}
          onClick={prevSlide}
          aria-label="Previous slide"
          disabled={currentSlide === 0}
        >
          <img src={ChevronRightIcon} className={styles.navButtonIconLeft} alt="" />
        </button>

        <div className={styles.cardsContainer}>
          {getCurrentSlideItems().map((item, index) => (
            <div key={keyExtractor(item, currentSlide * cardsPerSlide + index)}>
              {renderItem(item, currentSlide * cardsPerSlide + index)}
            </div>
          ))}
        </div>

        <button
          className={styles.navButton}
          onClick={nextSlide}
          aria-label="Next slide"
          disabled={currentSlide === totalSlides - 1}
        >
          <img src={ChevronRightIcon} alt="" />
        </button>
      </div>

      {totalSlides > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalSlides }, (_, index) => (
            <button
              key={index}
              className={`${styles.paginationDot} ${index === currentSlide ? styles.active : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

